<script setup>
</script>

<template>
  <!--    To add primary contact, user have no primary contact if from SSO login-->
  <app-dialog v-if="this.memberInfo.mobileList.length === 0" :title="$t(`label.addNewNumber`)" :dialog-show="addPrimaryNumberDialog.show" ref="addPrimaryNumberDialog" :closeAction="this.closeAddPrimaryNumberDialog" :max-width="400">
    <template>
      <v-form ref="primaryNumberRequestOtpForm">
        <captcha ref="addPrimaryNumberDialogCaptchaCode" v-model.trim="addPrimaryNumberDialog.captcha" :isOptional="true" :errorMsg="addPrimaryNumberDialog.captchaErrorMsg" :rules="validator.captchaRules()"></captcha>
        <app-form-field
            :enabledLabel="true"
            v-model.trim="addPrimaryNumberDialog.mobile"
            :label="$t(`field.firstNumber`)"
            :readonly="addPrimaryNumberDialog.primaryOtpRequested"
            class="otpPhoneNo pb-2"
            :rules="validator.mobileNumberRules(defaultLanguage)"
            :prefix="mobilePrefix"
        ></app-form-field>
      </v-form>
      <v-form ref="primaryNumberForm">
        <v-row no-gutters class="pb-5" style="align-items: center;">
          <v-col cols="5">
            <div class="mr-2">
              <v-btn
                  height="auto"
                  :disabled="this.$store.state.member.smsPrimaryVerificationSent.countDownTimer > 0"
                  @click="this.requestAddPrimaryContactSmsVerificationCode"
                  color="secondary"
                  depressed
                  class="otp-button theme-button font-weight-bold white--text mr-2"
              >
                {{ this.$store.state.member.smsPrimaryVerificationSent.countDownTimer > 0 ? formatSeconds(this.$store.state.member.smsPrimaryVerificationSent.countDownTimer) : $t(`button.requestOtp`) }}
              </v-btn>
            </div>
          </v-col>
          <v-col>
            <app-form-field :enabledLabel="false" :placeholder="$t(`fieldHint.fillUpHere`)" :rules="validator.requiredRules()" v-model.trim="addPrimaryNumberDialog.verifyCode" type="text"></app-form-field>
          </v-col>
        </v-row>
      </v-form>
      <v-row no-gutters class="d-flex justify-center align-center">
        <app-button
            class="otpSubmitButton white--text"
            :action="addPrimaryContact"
            :title="$t(`button.submit`)"
        ></app-button>
      </v-row>
    </template>
  </app-dialog>
</template>

<script>
import Captcha from "@/components/Captcha.vue";
import { SHARED } from "@/constants/constants";
import {
  MEMBER_ADD_PRIMARY_CONTACT,
  MEMBER_DETAIL,
  MEMBER_PHONE_VERIFICATION_STATUS,
  MEMBER_RESET_ADD_PRIMARY_CONTACT,
  MEMBER_RESET_SMS_REQUEST_PRIMARY,
  MEMBER_SMS_REQUEST_PRIMARY
} from "@/store/member.module";
import _ from "lodash";
import { formValidator, uiHelper } from "@/util";

export default {
  name: 'AddPrimaryContactDialog',
  components: {
    Captcha,
  },
  props: {
    mobileNumberPrefixList: {
      type: Array,
      required: true,
      default: () => []
    },
    personalInfo: {
      type: Object,
      required: true,
      default: () => {
        return {
          currency: '',
          memberCode: '',
        }
      }
    }
  },
  computed: {
    memberInfo() {
      return this.$store.state.member.info
    },
    mobilePrefix() {
      let self = this
      let prefix = _.find(this.mobileNumberPrefixList, function(p) {
        return p.value == self.memberInfo.currency
      })
      if (prefix) return prefix.text
      else return ''
    },
    smsPrimaryVerificationSent() {
      return this.$store.state.member.smsPrimaryVerificationSent.complete
    },
    addPrimaryNumberContact() {
      return this.$store.state.member.addPrimaryNumberContact.complete
    }
  },
  watch: {
    smsPrimaryVerificationSent() {
      let data = this.$store.state.member.smsPrimaryVerificationSent
      if (data.complete) {
        if (!data.success) {
          this.$emit('alert', data.success, data.message, 10)
          return
        }

        // OTP requested, should not allow user to change number anymore
        this.addPrimaryNumberDialog.primaryOtpRequested = true
      }
    },
    addPrimaryNumberContact() {
      let data = this.$store.state.member.addPrimaryNumberContact
      if (data.complete) {
        if (!data.success) {
          this.$emit('alert', data.success, data.message, 10)
          this.$store.dispatch(`${MEMBER_RESET_ADD_PRIMARY_CONTACT}`)
          return
        }

        this.$emit('alert', true, 'Phone added successfully', 10)
        this.closeAddPrimaryNumberDialog()
        this.$store.dispatch(`${MEMBER_RESET_ADD_PRIMARY_CONTACT}`)
        this.$store.dispatch(`${MEMBER_DETAIL}`)
        this.$store.dispatch(`${MEMBER_PHONE_VERIFICATION_STATUS}`, 0)

        this.resetDialogData()
      }
    }
  },
  data: () => ({
    validator: formValidator,
    defaultLanguage: SHARED.DEFAULT_LANGUAGE,
    addPrimaryNumberDialog: {
      show: false,
      mobile: '',
      primaryOtpRequested: false,
      verifyCode: '',
      captcha: '',
      captchaErrorMsg: '',
    },
  }),
  methods: {
    // Be called by parent component
    show() {
      this.addPrimaryNumberDialog.show = true
    },
    resetDialogData() {
      this.addPrimaryNumberDialog = {
        show: false,
        mobile: '',
        primaryOtpRequested: false,
        verifyCode: '',
        captcha: '',
        captchaErrorMsg: '',
      }
    },
    async addPrimaryContact() {
      if (this.$refs.primaryNumberForm.validate() && this.$refs.primaryNumberRequestOtpForm.validate()) {
        let filter = {
          verificationCode: this.addPrimaryNumberDialog.verifyCode,
          mobile: this.addPrimaryNumberDialog.mobile,
          captchaId: this.$refs.addPrimaryNumberDialogCaptchaCode.$refs.code.id,
          captchaCode: this.addPrimaryNumberDialog.captcha,
          membercode: this.personalInfo.memberCode,
        }

        this.$store.dispatch(`${MEMBER_ADD_PRIMARY_CONTACT}`, {
          filter
        })
      }
    },
    formatSeconds(secs) {
      const pad = n => (n < 10 ? `0${n}` : n)

      const h = Math.floor(secs / 3600)
      const m = Math.floor(secs / 60) - h * 60
      const s = Math.floor(secs - h * 3600 - m * 60)

      return `${pad(m)}:${pad(s)}`
    },
    closeAddPrimaryNumberDialog() {
      this.addPrimaryNumberDialog = {
        show: false,
        captcha: '',
        captchaErrorMsg: '',
        verifyCode: '',

        // These cannot reset by simple open/dismiss dialog
        primaryOtpRequested: this.addPrimaryNumberDialog.primaryOtpRequested,

        // Once OTP request, mobile number cannot change
        mobile: this.addPrimaryNumberDialog.primaryOtpRequested ? this.addPrimaryNumberDialog.mobile : '',

      }
    },
    requestAddPrimaryContactSmsVerificationCode() {
      if (!this.$refs.primaryNumberRequestOtpForm.validate()) {
        return
      }

      if (this.addPrimaryNumberDialog.captcha != '') {
        this.$store.dispatch(MEMBER_RESET_SMS_REQUEST_PRIMARY)

        let registerObj = {}
        registerObj.domain = uiHelper.getHostname()
        registerObj.language = uiHelper.getLanguage()
        registerObj.currency = this.personalInfo.currency
        registerObj.memberCode = this.personalInfo.memberCode
        registerObj.mobileNumber = this.addPrimaryNumberDialog.mobile
        registerObj.captchaId = this.$refs.addPrimaryNumberDialogCaptchaCode.$refs.code.id
        registerObj.captchaCode = this.addPrimaryNumberDialog.captcha

        this.$store.dispatch(`${MEMBER_SMS_REQUEST_PRIMARY}`, {
          registerObj
        })
      }
    },
  }
}
</script>

<style scoped lang="scss">
.otp-button {
  background-color: var(--v-primary-base);
  border-radius: 4px;
}
</style>